import type {
	SnipeTransferPlayerOutput,
	TransferPlayerState,
	readAllPlayersInput,
} from "@/api/custom_types";
import { ToolbarSwitcher } from "@/components/toolbar-switcher";
import { PAGINATION_PAGE_SIZE } from "@/constants";
import { useApiError } from "@/hooks";
import { useFacets } from "@/hooks/useFacetFilters";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import {
	TRANSFER_PLAYERS_FACET_SOURCE,
	useBatchUpdateTransferPlayers,
	useDeleteTransferPlayer,
	useDeleteTransferPlayersInBatch,
	useGetTransferPlayers,
	useRefreshPlayerPrices,
	useResetTransferPlayersSuccessRatio,
	useUpdateTransferPlayer,
} from "@/services";
import { cleanQuery, omit } from "@/utils/queryClient";
import { formatNumber } from "@/utils/tools";
import {
	ActionItem,
	ActionMenu,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	type ChipVariant,
	DataTable,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	Input,
	Label,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	Typography,
	toast,
	useDataTableRowSelection,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { RefreshPriceButton } from "./RefreshPriceButton";
import {
	type FilterFormData,
	STAdminTransferPlayersFilter,
} from "./STAdminTransferPlayersFilter";

type TransferPlayersListQuery = NonNullable<readAllPlayersInput["query"]>;

type TransferPlayerActivationLabelKey =
	| "admin.transferPlayers.activationState.active"
	| "admin.transferPlayers.activationState.deactivatedManually"
	| "admin.transferPlayers.activationState.deactivatedPriceUnfavorable";

type TransferPlayerActivationState = "ACTIVE" | "DEACTIVATED_MANUALLY";

const TRANSFER_PLAYER_ACTIVATION_STATE_CONFIG = {
	ACTIVE: {
		variant: "success",
		labelKey: "admin.transferPlayers.activationState.active",
	},
	DEACTIVATED_MANUALLY: {
		variant: "warning",
		labelKey: "admin.transferPlayers.activationState.deactivatedManually",
	},
	DEACTIVATED_TOO_MANY_ERROR: {
		variant: "warning",
		labelKey: "admin.transferPlayers.activationState.deactivatedManually",
	},
	DEACTIVATED_PRICE_UNFAVORABLE: {
		variant: "warning",
		labelKey:
			"admin.transferPlayers.activationState.deactivatedPriceUnfavorable",
	},
} as const satisfies Record<
	TransferPlayerState,
	{
		variant: ChipVariant;
		labelKey: TransferPlayerActivationLabelKey;
	}
>;

interface TransferPlayerRowActionProps {
	player: SnipeTransferPlayerOutput;
}

type PercentField = { value: string; useGlobal: boolean };

const initialPercentField = (
	player: SnipeTransferPlayerOutput | undefined,
	local: number | null | undefined,
): PercentField =>
	player
		? { value: local == null ? "" : String(local), useGlobal: local == null }
		: { value: "", useGlobal: false };

/** `undefined` = leave unchanged, `null` = use the global setting */
const percentFieldToBody = ({ value, useGlobal }: PercentField) => {
	if (useGlobal) return null;
	return value.trim() === "" ? undefined : Number(value);
};

/**
 * Edits sell/snipe % for one player (prefilled) or many (empty = unchanged).
 * Mount it only while open so it starts from the latest row values.
 */
const EditPercentagesDialog: React.FC<{
	ids: string[];
	player?: SnipeTransferPlayerOutput;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}> = ({ ids, player, open, onOpenChange, onSuccess }) => {
	const { t } = useTranslation();
	const updateInBatch = useBatchUpdateTransferPlayers();
	const [sell, setSell] = useState(() =>
		initialPercentField(player, player?.sell_percentage),
	);
	const [snipe, setSnipe] = useState(() =>
		initialPercentField(player, player?.snipe_percentage),
	);

	const fields = [
		{
			key: "sell",
			label: t("admin.transferPlayers.create.sellPercentageLabel"),
			state: sell,
			setState: setSell,
			applied: player?.applied_sell_percentage,
		},
		{
			key: "snipe",
			label: t("admin.transferPlayers.create.snipePercentageLabel"),
			state: snipe,
			setState: setSnipe,
			applied: player?.applied_snipe_percentage,
		},
	];

	const handleSubmit = () => {
		updateInBatch.mutate(
			{
				ids,
				sell_percentage: percentFieldToBody(sell),
				snipe_percentage: percentFieldToBody(snipe),
			},
			{
				onSuccess: () => {
					toast.success(
						t("admin.transferPlayers.toast.percentagesUpdateSuccess", {
							value: ids.length,
						}),
					);
					onOpenChange(false);
					onSuccess?.();
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("admin.transferPlayers.editPercentages")}
					</DialogTitle>
					<DialogDescription>
						{player
							? t("admin.transferPlayers.editPercentagesHint")
							: t("admin.transferPlayers.editPercentagesBatchHint", {
									value: ids.length,
								})}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 px-4">
					{fields.map(({ key, label, state, setState, applied }) => (
						<div key={key} className="flex flex-col gap-1.5">
							<Label htmlFor={`percent-${key}`}>{label}</Label>
							<Input
								id={`percent-${key}`}
								type="number"
								min={0}
								max={100}
								disabled={state.useGlobal}
								value={state.useGlobal ? "" : state.value}
								placeholder={
									state.useGlobal && applied != null
										? `${t(
												"admin.transferPlayers.table.sourceLabelGlobal",
											)}: ${applied}%`
										: player
											? ""
											: t("admin.transferPlayers.unchangedPlaceholder")
								}
								onChange={(event) =>
									setState((prev) => ({ ...prev, value: event.target.value }))
								}
							/>
							<Label className="flex items-center gap-2">
								<Checkbox
									checked={state.useGlobal}
									onCheckedChange={(checked) =>
										setState((prev) => ({
											...prev,
											useGlobal: checked === true,
										}))
									}
								/>
								{t("admin.transferPlayers.useGlobal")}
							</Label>
						</div>
					))}
				</div>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("common.button.no"),
							props: { type: "button", onClick: () => onOpenChange(false) },
						},
						primary: {
							label: t("action.save"),
							props: {
								type: "button",
								loading: updateInBatch.isPending,
								onClick: handleSubmit,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

const ResetSuccessRatioDialog: React.FC<{
	ids: string[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}> = ({ ids, open, onOpenChange, onSuccess }) => {
	const { t } = useTranslation();
	const resetSuccessRatio = useResetTransferPlayersSuccessRatio();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					<DialogDescription>
						{t("admin.transferPlayers.resetSuccessRatioConfirm", {
							value: ids.length,
						})}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("common.button.no"),
							props: { type: "button", onClick: () => onOpenChange(false) },
						},
						primary: {
							label: t("admin.transferPlayers.resetSuccessRatio"),
							props: {
								type: "button",
								loading: resetSuccessRatio.isPending,
								onClick: () =>
									resetSuccessRatio.mutate(
										{ body: { ids } },
										{
											onSuccess: () => {
												onOpenChange(false);
												onSuccess?.();
											},
										},
									),
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

const TransferPlayerRowAction: React.FC<TransferPlayerRowActionProps> = ({
	player,
}) => {
	const { t } = useTranslation();
	const { id: playerId, activation_state: activationState } = player;
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const deletePlayer = useDeleteTransferPlayer();
	const updateTransferPlayer = useUpdateTransferPlayer();
	const refreshPrices = useRefreshPlayerPrices();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [activationDialog, setActivationDialog] = useState<boolean>(false);
	const [resetDialog, setResetDialog] = useState<boolean>(false);

	const nextActivationState: TransferPlayerState =
		activationState === "ACTIVE" ? "DEACTIVATED_MANUALLY" : "ACTIVE";
	const isActivating = nextActivationState === "ACTIVE";

	const handleDeleteConfirm = () => {
		deletePlayer.mutate(
			{ path: { transfer_player_id: playerId } },
			{
				onSuccess: () => {
					setDeleteDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const handleActivationConfirm = () => {
		updateTransferPlayer.mutate(
			{
				path: { transfer_player_id: playerId },
				body: { activation_state: nextActivationState },
			},
			{
				onSuccess: () => {
					setActivationDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					{isActivating ? (
						<ActionItem
							leadingIconName="play-circle-line"
							loading={updateTransferPlayer.isPending}
							onClick={() => {
								setMenuActionOpen(false);
								setActivationDialog(true);
							}}
						>
							{t("action.play")}
						</ActionItem>
					) : (
						<ActionItem
							leadingIconName="pause-circle-line"
							loading={updateTransferPlayer.isPending}
							onClick={() => {
								setMenuActionOpen(false);
								setActivationDialog(true);
							}}
						>
							{t("action.pause")}
						</ActionItem>
					)}
					<ActionItem
						leadingIconName="file-copy-line"
						onClick={async () => {
							setMenuActionOpen(false);
							try {
								await navigator.clipboard.writeText(playerId);
								toast.success(t("action.idCopied"));
							} catch {
								toast.error(t("error.unexpected"));
							}
						}}
					>
						{t("action.copyId")}
					</ActionItem>
					<ActionItem
						leadingIconName="price-tag-3-line"
						loading={refreshPrices.isPending}
						onClick={() =>
							refreshPrices.mutate(
								{ body: { resource_ids: [player.resource_id] } },
								{ onSettled: () => setMenuActionOpen(false) },
							)
						}
					>
						{t("admin.transferPlayers.refreshPrice")}
					</ActionItem>
					<ActionItem
						leadingIconName="edit-line"
						onClick={() => {
							setMenuActionOpen(false);
							setEditDialog(true);
						}}
					>
						{t("admin.transferPlayers.editPercentages")}
					</ActionItem>
					<ActionItem
						leadingIconName="refresh-line"
						onClick={() => {
							setMenuActionOpen(false);
							setResetDialog(true);
						}}
					>
						{t("admin.transferPlayers.resetSuccessRatio")}
					</ActionItem>
					<ActionItem
						leadingIconName="delete-bin-line"
						loading={deletePlayer.isPending}
						onClick={() => {
							setMenuActionOpen(false);
							setDeleteDialog(true);
						}}
					>
						{t("action.delete")}
					</ActionItem>
				</ActionMenu>
			</div>

			<Dialog open={activationDialog} onOpenChange={setActivationDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<Typography variant="body-md">
							{isActivating
								? t("admin.transferPlayers.activateConfirm")
								: t("admin.transferPlayers.deactivateConfirm")}
						</Typography>
					</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setActivationDialog(false),
								},
							},
							primary: {
								label: isActivating
									? t("action.button.play")
									: t("action.button.pause"),
								props: {
									type: "button",
									loading: updateTransferPlayer.isPending,
									onClick: handleActivationConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* Mounted only while open so the inputs start from the latest row values */}
			{editDialog && (
				<EditPercentagesDialog
					ids={[playerId]}
					player={player}
					open={editDialog}
					onOpenChange={setEditDialog}
				/>
			)}

			<ResetSuccessRatioDialog
				ids={[playerId]}
				open={resetDialog}
				onOpenChange={setResetDialog}
			/>

			<Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t("admin.transferPlayers.deleteConfirm")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setDeleteDialog(false),
								},
							},
							primary: {
								label: t("action.button.delete"),
								props: {
									type: "button",
									loading: deletePlayer.isPending,
									onClick: handleDeleteConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

const STAdminTransferPlayers = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = usePersistedServerTableState("st-admin-transfer-players", {
		initialPageSize: PAGINATION_PAGE_SIZE,
	});
	const rowSelection =
		useDataTableRowSelection<SnipeTransferPlayerOutput>(true);
	const [isBatchResetDialogOpen, setIsBatchResetDialogOpen] = useState(false);
	const [isBatchEditDialogOpen, setIsBatchEditDialogOpen] = useState(false);
	const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false);
	const [batchActivationState, setBatchActivationState] =
		useState<TransferPlayerActivationState | null>(null);

	const [filters, setFilters] = useState<FilterFormData>({
		activationState: [],
		rarityName: [],
		clubName: [],
		leagueName: [],
		nationName: [],
	});

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			setFilters(values);
			server.onPageChange(0);
		},
		[server],
	);

	const listQuery = cleanQuery<TransferPlayersListQuery>({
		page: server.pageIndex + 1,
		page_size: server.pageSize,
		player_nick_name: filters.playerNickname,
		activation_states: filters.activationState as TransferPlayerState[],
		club_names: filters.clubName,
		rarity_names: filters.rarityName,
		league_names: filters.leagueName,
		nation_names: filters.nationName,
	});

	const facetsFilters = omit(listQuery, ["page", "page_size"] as const);

	const {
		data: transferPlayers,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetTransferPlayers({ query: listQuery });

	const facetsCounts = useFacets({
		source: TRANSFER_PLAYERS_FACET_SOURCE,
		filters: facetsFilters,
	});

	const deleteTransferPlayersInBatch = useDeleteTransferPlayersInBatch();
	const updateTransferPlayersInBatch = useBatchUpdateTransferPlayers();

	const handleBatchDeleteSubmit = () => {
		deleteTransferPlayersInBatch.mutate(
			{ body: { ids: rowSelection.selectedIds } },
			{
				onSuccess: () => {
					setIsBatchDeleteDialogOpen(false);
					rowSelection.clear();
					refetch();
				},
			},
		);
	};
	const batchActivationIds = rowSelection.selectedItems
		.filter((player) =>
			batchActivationState === "DEACTIVATED_MANUALLY"
				? player.activation_state === "ACTIVE"
				: player.activation_state !== "ACTIVE",
		)
		.map((player) => player.id);
	const activeSelectedCount = rowSelection.selectedItems.filter(
		(player) => player.activation_state === "ACTIVE",
	).length;
	const inactiveSelectedCount =
		rowSelection.selectedItems.length - activeSelectedCount;
	const isBatchPausing = batchActivationState === "DEACTIVATED_MANUALLY";
	const handleBatchActivationSubmit = () => {
		if (!batchActivationState) return;
		updateTransferPlayersInBatch.mutate(
			{ ids: batchActivationIds, activation_state: batchActivationState },
			{
				onSuccess: () => {
					toast.success(
						t(
							isBatchPausing
								? "admin.transferPlayers.toast.deactivateSelectedSuccess"
								: "admin.transferPlayers.toast.activateSelectedSuccess",
							{ value: batchActivationIds.length },
						),
					);
					setBatchActivationState(null);
					rowSelection.clear();
					refetch();
				},
			},
		);
	};

	const rows = transferPlayers?.results ?? [];
	const rowCount = transferPlayers?.count ?? rows.length;

	const formatPriceValue = useCallback(
		(value: number | null | undefined) =>
			value == null ? "-" : formatNumber(value, "count"),
		[],
	);

	const formatPercentValue = useCallback(
		(value: number | null | undefined) =>
			value == null ? "-" : formatNumber(value, "percent"),
		[],
	);

	const columns = useMemo<ColumnDef<SnipeTransferPlayerOutput>[]>(
		() => [
			{
				id: "nickname",
				header: t("admin.transferPlayers.table.nickname"),
				enableSorting: false,
				cell: ({ row }) => {
					const nickname = row.original.player_metadata.nick_name ?? "-";
					const rating =
						row.original.player_metadata.rating !== undefined
							? row.original.player_metadata.rating
							: "";
					return `${nickname}${rating !== "" ? ` ${rating}` : ""}`;
				},
			},
			{
				id: "activation_state",
				header: t("admin.transferPlayers.table.activationState"),
				enableSorting: false,
				cell: ({ row }) => {
					const config =
						TRANSFER_PLAYER_ACTIVATION_STATE_CONFIG[
							row.original.activation_state
						];

					return (
						<Chip variant={config.variant} size="sm">
							{t(config.labelKey)}
						</Chip>
					);
				},
			},
			{
				id: "last_price",
				header: t("admin.transferPlayers.table.lastPrice"),
				enableSorting: false,
				cell: ({ row }) =>
					formatPriceValue(row.original.player_metadata.last_price),
			},
			{
				id: "price_range",
				header: t("admin.transferPlayers.table.priceRange"),
				enableSorting: false,
				cell: ({ row }) => {
					const minPriceRange = formatPriceValue(
						row.original.player_metadata.min_price_range,
					);
					const maxPriceRange = formatPriceValue(
						row.original.player_metadata.max_price_range,
					);
					return `${minPriceRange} - ${maxPriceRange}`;
				},
			},
			{
				id: "picture",
				header: t("admin.transferPlayers.table.picture"),
				enableSorting: false,
				cell: ({ row }) => {
					const src = row.original.player_metadata.picture;
					if (!src) return "-";
					return (
						<div className="w-18">
							<img
								src={src}
								alt={row.original.player_metadata.nick_name ?? "player"}
								loading="lazy"
								decoding="async"
								referrerPolicy="no-referrer"
								className="h-24 w-full rounded-md object-cover"
							/>
						</div>
					);
				},
			},
			{
				id: "rarity",
				header: t("admin.transferPlayers.table.rarity"),
				enableSorting: false,
				cell: ({ row }) => row.original.player_metadata.rarity_name || "-",
			},
			{
				id: "sell_percentage",
				header: t("admin.transferPlayers.table.sellPercentage"),
				enableSorting: false,
				cell: ({ row }) => (
					<AppliedPercentCell
						local={row.original.sell_percentage}
						applied={row.original.applied_sell_percentage}
						price={row.original.sell_price}
					/>
				),
			},
			{
				id: "snipe_percentage",
				header: t("admin.transferPlayers.table.snipePercentage"),
				enableSorting: false,
				cell: ({ row }) => (
					<AppliedPercentCell
						local={row.original.snipe_percentage}
						applied={row.original.applied_snipe_percentage}
						price={row.original.best_snipe_price}
					/>
				),
			},
			{
				id: "success_ratio",
				header: t("admin.transferPlayers.table.successRatio"),
				enableSorting: false,
				cell: ({ row }) => {
					const ratio = row.original.success_ratio;
					if (ratio == null) return "-";
					return (
						<span className={`tabular-nums ${successRatioClassName(ratio)}`}>
							{formatPercentValue(ratio)}
						</span>
					);
				},
			},
			{
				id: "transfers_last_7_days",
				header: t("admin.transferPlayers.table.transfersLast7Days"),
				enableSorting: false,
				cell: ({ row }) => formatPriceValue(row.original.transfers_last_7_days),
			},
			{
				id: "resource_id",
				header: t("admin.transferPlayers.table.resourceId"),
				enableSorting: false,
				cell: ({ row }) => row.original.resource_id,
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => <TransferPlayerRowAction player={row.original} />,
			},
		],
		[t, formatPriceValue, formatPercentValue],
	);

	return (
		<div className="flex flex-col gap-6">
			<ToolbarSwitcher
				hasSelection={rowSelection.selectedIds.length > 0}
				filterComponent={
					<STAdminTransferPlayersFilter
						defaultValues={filters}
						facetCounts={facetsCounts}
						onSearch={handleSearch}
					/>
				}
				actionComponent={
					<Card>
						<CardContent className="flex flex-wrap items-center justify-between gap-3">
							<Typography variant="title-sm">
								{t("admin.transferPlayers.selectedPlayers", {
									value: rowSelection.selectedIds.length,
								})}
							</Typography>
							<div className="flex flex-wrap gap-2">
								<RefreshPriceButton
									resourceIds={rowSelection.selectedItems.map(
										(player) => player.resource_id,
									)}
									onSuccess={rowSelection.clear}
								/>
								<Button
									type="button"
									startIcon={<Icon name="pause-circle-line" />}
									disabled={activeSelectedCount === 0}
									onClick={() =>
										setBatchActivationState("DEACTIVATED_MANUALLY")
									}
								>
									{t("action.pause")}
								</Button>
								<Button
									type="button"
									startIcon={<Icon name="play-circle-line" />}
									disabled={inactiveSelectedCount === 0}
									onClick={() => setBatchActivationState("ACTIVE")}
								>
									{t("action.play")}
								</Button>
								<Button
									type="button"
									startIcon={<Icon name="edit-line" />}
									onClick={() => setIsBatchEditDialogOpen(true)}
								>
									{t("admin.transferPlayers.editPercentages")}
								</Button>
								<Button
									type="button"
									startIcon={<Icon name="refresh-line" />}
									onClick={() => setIsBatchResetDialogOpen(true)}
								>
									{t("admin.transferPlayers.resetSuccessRatio")}
								</Button>
								<Button
									type="button"
									color="danger"
									startIcon={<Icon name="delete-bin-line" />}
									onClick={() => setIsBatchDeleteDialogOpen(true)}
								>
									{t("admin.transferPlayers.deleteSelected")}
								</Button>
							</div>
						</CardContent>
					</Card>
				}
			/>

			<DataTable
				columns={columns}
				data={rows}
				totalItems={rowCount}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
				rowSelection={rowSelection}
			/>
			{isBatchEditDialogOpen && (
				<EditPercentagesDialog
					ids={rowSelection.selectedIds}
					open={isBatchEditDialogOpen}
					onOpenChange={setIsBatchEditDialogOpen}
					onSuccess={rowSelection.clear}
				/>
			)}
			<ResetSuccessRatioDialog
				ids={rowSelection.selectedIds}
				open={isBatchResetDialogOpen}
				onOpenChange={setIsBatchResetDialogOpen}
				onSuccess={rowSelection.clear}
			/>
			<Dialog
				open={isBatchDeleteDialogOpen}
				onOpenChange={setIsBatchDeleteDialogOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t("admin.transferPlayers.deleteSelectedConfirm", {
								value: rowSelection.selectedIds.length,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setIsBatchDeleteDialogOpen(false),
								},
							},
							primary: {
								label: t("action.button.delete"),
								props: {
									type: "button",
									loading: deleteTransferPlayersInBatch.isPending,
									onClick: handleBatchDeleteSubmit,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
			<Dialog
				open={batchActivationState !== null}
				onOpenChange={(open) => !open && setBatchActivationState(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t(
								isBatchPausing
									? "admin.transferPlayers.deactivateSelectedConfirm"
									: "admin.transferPlayers.activateSelectedConfirm",
								{ value: batchActivationIds.length },
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setBatchActivationState(null),
								},
							},
							primary: {
								label: t(isBatchPausing ? "action.pause" : "action.play"),
								props: {
									type: "button",
									loading: updateTransferPlayersInBatch.isPending,
									onClick: handleBatchActivationSubmit,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default STAdminTransferPlayers;

function AppliedPercentCell({
	local,
	applied,
	price,
}: {
	local: number | null;
	applied?: number | null;
	price?: number | null;
}) {
	const { t } = useTranslation();
	const percent = applied ?? local;
	if (percent == null) return "-";
	const isLocal = local != null;

	return (
		<div className="flex flex-col gap-0.5">
			<span className="inline-flex items-center gap-1">
				<span className="tabular-nums">{formatNumber(percent, "percent")}</span>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex cursor-help">
							<Chip variant={isLocal ? "info" : "muted"} size="sm">
								<span className="inline-flex items-center gap-0.5">
									{t(
										isLocal
											? "admin.transferPlayers.table.sourceLabelCustom"
											: "admin.transferPlayers.table.sourceLabelGlobal",
									)}
									<Icon
										name="information-line"
										size="xs"
										className="opacity-70"
									/>
								</span>
							</Chip>
						</span>
					</TooltipTrigger>
					<TooltipContent>
						<p>
							{t(
								isLocal
									? "admin.transferPlayers.table.sourceTooltipCustom"
									: "admin.transferPlayers.table.sourceTooltipGlobal",
							)}
						</p>
						<p>{t("admin.transferPlayers.table.sourceTooltipHint")}</p>
					</TooltipContent>
				</Tooltip>
			</span>
			{price != null && (
				<span className="text-secondary-text-100 text-xs tabular-nums">
					{formatNumber(price, "count")}
				</span>
			)}
		</div>
	);
}

function successRatioClassName(ratio: number): string {
	if (ratio > 90) return "text-success-text";
	if (ratio > 80) return "text-yellow-500";
	if (ratio > 70) return "text-orange-500";
	return "text-danger-text";
}
